<template>
	<div class="d-flex align-center">
		<span v-if="modelValue.axis" class="axis-label" color="primary">
			<v-icon size="x-small"> mdi-chart-line </v-icon>
			{{ modelValue.axis }}
		</span>
		<span
			v-else-if="modelValue.fromBroadcast && modelValue.datasetUuid"
			class="axis-label"
			title="Per-branch default tag"
		>
			<v-icon size="x-small"> mdi-chart-line-variant </v-icon>
		</span>
		<span
			v-else-if="modelValue.fromBroadcast"
			class="axis-label"
			title="Graph-wide default tag"
		>
			<v-icon size="x-small"> mdi-chart-line </v-icon>
		</span>
		<v-combobox
			ref="valueField"
			v-model:search="valueSuggestionSearch"
			autocomplete="off"
			:autofocus="autoFocus()"
			:class="['metadata-value-field', { readonly: readOnly }]"
			color="secondaryDark"
			:custom-filter="filterSuggestions"
			density="compact"
			:disabled="mode === Mode.BLANK_TABLE_CELL"
			hide-details="auto"
			hide-no-data
			:items="suggestions"
			label="Value"
			:menu-props="{
				value:
					!readOnly &&
					!(valueSuggestionSearch === '' || !isNaN(valueSuggestionSearch)),
				location: 'bottom',
				right: true,
				disabled: readOnly || !suggestions || suggestions.length === 0,
				contentProps: {
					onKeydown: (event) => {
						event.stopPropagation();
					},
				},
			}"
			:model-value="modelValue.value"
			:readonly="readOnly"
			:rules="getValidationRules(modelValue.index)"
			single-line
			style="min-width: 0; max-width: 100%; width: 100%"
			@update:model-value="updateValue"
		>
			<template v-if="showValueSuggestionText()" #prepend-item>
				<v-list-subheader> Is this the same as: </v-list-subheader>
			</template>
			<template #append>
				<v-icon
					v-if="!readOnly && !!selectTextFunction"
					ref="textSelectIcon"
					class="ma-0"
					size="small"
					@mousedown.prevent
					@click.stop="selectText($event)"
				>
					mdi-form-textbox
				</v-icon>
				<span v-else style="width: 22px" />
			</template>
		</v-combobox>

		<TextSanitizationDialog ref="sanitizationDialog" />
	</div>
</template>

<script>
import _ from "lodash";
import { useLDFlag } from "launchdarkly-vue-client-sdk";

import { Mode } from "./VMetadataConstants.js";
import TextSanitizationDialog from "./TextSanitizationDialog.vue";

export default {
	name: "VMetadataValueField",

	components: {
		TextSanitizationDialog,
	},

	inject: [
		"autoFocus",
		"dialogKeydownHandler",
		"getValidationRules",
		"isKeyTag",
		"textSelect",
	],

	props: {
		bounds: { type: Object, default: null },
		directTextSelect: { type: Boolean, default: false },
		metadata: { type: Array, default: () => [] },
		mode: { type: String, default: Mode.STANDARD },
		modelValue: { type: Object, default: null },
		readOnly: { type: Boolean, default: false },
		selectTextFunction: { type: Function, default: null },
		suggestionMap: { type: Object, default: null },
		tagGroup: { type: Object, default: null },
	},

	emits: ["update:modelValue", "update:location"],

	setup() {
		const textSelectCharacterSanitization = useLDFlag(
			"text-select-character-sanitization",
		);

		return {
			textSelectCharacterSanitization,
		};
	},

	data() {
		return {
			valueSuggestionSearch: "",
			Mode,
		};
	},
	computed: {
		suggestions() {
			return this.getSuggestions();
		},
	},

	methods: {
		async showSanitizationDialog(
			originalText,
			sanitizedText,
			replacementDetails,
			sourceImages = [],
		) {
			const result = await this.$refs.sanitizationDialog.open(
				originalText,
				sanitizedText,
				replacementDetails,
				sourceImages,
			);

			return result;
		},

		async selectText(event) {
			if (typeof this.selectTextFunction === "function") {
				const container = event.target.closest?.(".v-overlay-container");
				if (!container) return;

				// call text selection function
				let selectTextTool;
				const cancellable = this.textSelect.begin(
					(resolve, keydownHandler) => {
						if (this.selectTextFunction) {
							selectTextTool = this.selectTextFunction(resolve, keydownHandler);
						}
					},
					this.dialogKeydownHandler,
					{ bounds: this.bounds, container },
					{ exitOnClose: this.directTextSelect },
				);

				try {
					const result = await cancellable.promise;
					let {
						text,
						originalText,
						wasSanitized,
						replacementDetails,
						location,
						sourceImages,
					} = result;

					if (
						this.textSelectCharacterSanitization &&
						wasSanitized &&
						originalText
					) {
						if (selectTextTool?.detach) selectTextTool.detach();
						this.textSelect.finish();

						const result = await this.showSanitizationDialog(
							originalText,
							text,
							replacementDetails,
							sourceImages,
						);

						if (!result.confirmed) {
							return;
						}

						text = result.text;
					} else {
						if (selectTextTool?.detach) selectTextTool.detach();
						this.textSelect.finish();
					}

					if (text) {
						this.updateValue(text);
					}
					if (
						location &&
						![
							Mode.TABLE_HEADER,
							Mode.BAR_GRAPH_HEADER,
							Mode.TABLE_CELL,
							Mode.BRANCH_EDIT,
						].includes(this.mode)
					) {
						this.updateLocation(location);
					}
				} catch (error) {
					if (selectTextTool?.detach) selectTextTool.detach();
				}

				// set focus back on value input of the row
				event.target
					.closest(".v-input")
					?.querySelector("input[type='text']")
					?.focus();

				// clear the cancellable
				this.textSelect.finish();
			}
		},

		getSuggestions() {
			const tag = this.modelValue.tag;
			if (tag && this.suggestionMap && this.suggestionMap.has(tag.uuid)) {
				return _.uniq(
					this.suggestionMap
						.get(tag.uuid)
						.filter(
							(suggestion) => suggestion.tagGroupUuid === this.tagGroup?.uuid,
						)
						.sort((a, b) => {
							return Number(b.isGraphMatch) - Number(a.isGraphMatch);
						})
						.map((suggestion) => suggestion.value),
				);
			}

			return [];
		},

		filterSuggestions(item, queryText, itemText) {
			if (queryText.length > 0) {
				return (
					itemText.title
						.toLocaleLowerCase()
						.indexOf(queryText.toLocaleLowerCase()) > -1
				);
			}
			return false;
		},

		showValueSuggestionText() {
			return this.suggestionMap && this.suggestionMap.size > 0;
		},

		updateValue(value) {
			this.$emit("update:modelValue", {
				...this.modelValue,
				value,
			});
		},

		updateLocation(location) {
			this.$emit("update:location", location);
		},
	},
};
</script>

<style lang="scss">
.axis-label {
	font-size: 14px;
	font-weight: bold;
	width: 27px;
}
.metadata-value-field .v-input__append {
	margin-inline-start: 0 !important;
}
.metadata-value-field.readonly .v-field__append-inner {
	display: none;
}
.metadata-value-field.readonly .v-overlay {
	display: none !important;
}
.v-field__overlay {
	opacity: 0 !important;
	background-color: white !important;
}
</style>
